import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, Send, Info } from 'lucide-react';
import tapstarLogo from '@/img/tapstar_logo.png';
import { useToast } from '@/components/ui/use-toast';
import { authService } from '@/api/authService';
import { useLanguage } from '@/components/LanguageContext';

const PasswordReset = () => {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const { toast } = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await authService.requestPasswordReset(email);
            setIsSent(true);
            toast({
                title: t('email_sent_title'),
                description: t('email_instructions_sent'),
            });
        } catch (error) {
            toast({
                variant: "destructive",
                title: t('error_label'),
                description: t('unexpected_error_reset'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (isSent) {
        return (
            <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-4">
                <Card className="w-full max-w-md border border-slate-200 shadow-2xl bg-white rounded-[2rem] overflow-hidden p-10 text-center space-y-6">
                    <div className="flex justify-center">
                        <div className="h-20 w-20 bg-emerald-50 rounded-full flex items-center justify-center">
                            <Send className="h-10 w-10 text-emerald-500" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <CardTitle className="text-2xl font-bold text-slate-900">{t('email_sent_title')}</CardTitle>
                        <CardDescription className="text-slate-500 text-base">
                            {t('email_sent_desc')} <strong>{email}</strong>.
                        </CardDescription>
                    </div>
                    <Button asChild className="w-full h-14 bg-[#feae2c] hover:bg-[#e69a1a] text-white font-bold rounded-2xl transition-all">
                        <Link to="/login">{t('back_to_login_btn')}</Link>
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 p-4">
            <Card className="w-full max-w-md border border-slate-200 shadow-2xl bg-white rounded-[2rem] overflow-hidden">
                <CardHeader className="space-y-4 pt-10 pb-6 text-center">
                    <div className="flex justify-center mb-2">
                        <img src={tapstarLogo} alt="Tapstar Logo" className="h-16 w-auto object-contain" />
                    </div>
                    <div className="space-y-2">
                        <CardTitle className="text-2xl font-bold tracking-tight text-slate-900">{t('reset_password_title')}</CardTitle>
                        <CardDescription className="text-slate-500 text-sm">{t('reset_password_desc')}</CardDescription>
                    </div>
                </CardHeader>

                <CardContent className="px-8 pb-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-[10px] font-bold text-slate-900 flex items-center gap-1 uppercase tracking-wider">
                                <Mail className="h-3 w-3" /> {t('userEmail')}
                            </Label>
                            <div className="relative group">
                                <Input
                                    id="email"
                                    placeholder="Ingresa tu correo registrado"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="h-14 rounded-2xl border-slate-200 bg-white px-4 transition-all"
                                    required
                                />
                            </div>
                            <p className="text-[10px] text-slate-500 flex items-start gap-1 pt-1">
                                <Info className="h-3 w-3 mt-0.5 shrink-0" />
                                Te enviaremos un enlace para restablecer tu contraseña
                            </p>
                        </div>

                        <Button
                            type="submit"
                            className="w-full h-14 bg-[#feae2c] hover:bg-[#e69a1a] text-white font-bold rounded-2xl shadow-md transition-all uppercase tracking-wide text-sm flex items-center justify-center gap-2"
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                                <>
                                    <Send className="h-5 w-5" />
                                    {t('send_instructions_btn')}
                                </>
                            )}
                        </Button>
                    </form>

                    <div className="mt-8 space-y-4 text-center">
                        <p className="text-xs text-slate-500">
                            {t('already_remember_password')} <Link to="/login" className="font-bold text-orange-400 hover:underline">{t('login_button')}</Link>
                        </p>
                        <p className="text-xs text-slate-500">
                            {t('no_account')} <Link to="/register" className="font-bold text-orange-400 hover:underline">{t('register_now')}</Link>
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default PasswordReset;
